/**
 * Single source of truth for how a segment is named across the entire app.
 *
 * Design principle: show the NAME of the thing being done, not the vendor
 * who brokered the booking. The vendor (with its logo) is shown separately
 * via <VendorLogo /> or <SegmentVendorBadge />.
 *
 * Fallback chain: purpose-specific name → vendor_name → generic type label.
 * This preserves backwards compatibility with legacy segments that stored
 * the vendor as the title.
 */

const TYPE_DEFAULTS = {
  flight: "Flight",
  accommodation: "Accommodation",
  cruise: "Cruise",
  train: "Train",
  bus: "Bus",
  car_rental: "Car Rental",
  rideshare: "Taxi/Rideshare",
  activity: "Activity",
  event: "Event",
  site_plan: "Activity",
  other: "Segment",
};

/**
 * Primary title — the "thing" you are doing.
 * Examples:
 *   flight:         "AirAsia"
 *   accommodation:  "Tokyu Stay Ginza Hotel"
 *   cruise:         "Royal Caribbean" (cruise_line_name) — falls back to vendor
 *   rideshare:      "Taxi/Rideshare"
 *   car_rental:     "Car Rental"
 *   activity:       "Shibuya Sky Ticket"
 */
export function getSegmentTitle(segment) {
  if (!segment) return "Segment";
  const type = segment.type;

  switch (type) {
    case "flight":
      // For flights, the airline (vendor) *is* the "thing" — same entity.
      return segment.vendor_name || TYPE_DEFAULTS.flight;

    case "accommodation":
      return (
        segment.property_name ||
        segment.vendor_name ||
        TYPE_DEFAULTS.accommodation
      );

    case "cruise":
      return (
        segment.cruise_line_name ||
        segment.vendor_name ||
        TYPE_DEFAULTS.cruise
      );

    case "train":
      return (
        segment.train_line_name ||
        segment.vendor_name ||
        TYPE_DEFAULTS.train
      );

    case "bus":
      return segment.vendor_name || TYPE_DEFAULTS.bus;

    case "rideshare":
      // Always named by activity, never by vendor (Uber/Didi → logo only)
      return TYPE_DEFAULTS.rideshare;

    case "car_rental":
      return TYPE_DEFAULTS.car_rental;

    case "activity":
    case "event":
    case "site_plan":
      return (
        segment.activity_name ||
        segment.vendor_name ||
        TYPE_DEFAULTS[type]
      );

    default:
      return segment.vendor_name || TYPE_DEFAULTS.other;
  }
}

/**
 * Secondary descriptor — route / location / flight number.
 * Intentionally short; callers decide whether to render it.
 */
export function getSegmentSubtitle(segment) {
  if (!segment) return "";
  const type = segment.type;

  switch (type) {
    case "flight":
      if (segment.flight_number) return `#${segment.flight_number}`;
      return "";

    case "train":
    case "bus":
      if (segment.from_location && segment.to_location) {
        return `${segment.from_location} → ${segment.to_location}`;
      }
      if (segment.flight_number) return `#${segment.flight_number}`; // train/bus number stored here legacy
      return "";

    case "rideshare":
    case "car_rental":
      if (segment.from_location && segment.to_location) {
        return `${segment.from_location} → ${segment.to_location}`;
      }
      if (segment.from_location) return `From ${segment.from_location}`;
      if (segment.to_location) return `To ${segment.to_location}`;
      return "";

    case "accommodation":
      return segment.accommodation_location || "";

    case "cruise":
      return segment.accommodation_location || segment.route || "";

    case "activity":
    case "event":
    case "site_plan":
      return segment.accommodation_location || "";

    default:
      return "";
  }
}

/**
 * The name that should appear on the vendor logo chip next to the title.
 * Always the booking/operating company (may be an aggregator like Booking.com).
 */
export function getSegmentVendor(segment) {
  return (segment && segment.vendor_name) || "";
}

/**
 * Decide whether the vendor chip should be shown alongside the title.
 * Hidden when the title and vendor are the same string (e.g. flights where
 * the airline is both the title and the vendor — avoid visual redundancy).
 */
export function shouldShowVendorChip(segment) {
  if (!segment) return false;
  const vendor = getSegmentVendor(segment);
  if (!vendor) return false;
  const title = getSegmentTitle(segment);
  return title.trim().toLowerCase() !== vendor.trim().toLowerCase();
}
